import { Flex, Label, Text } from "@coaching-culture/ui";
import { useUser } from "auth";
import { useOrg } from "auth/OrgProvider";
import axios from "axios";
import { useEffect, useState } from "react";
import { FaBell } from "react-icons/fa";
import { Route } from "react-router";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { space, SpaceProps } from "styled-system";
import AccountMenu from "../components/AccountMenu";
import logoSrc from "../img/logo.png";
import { LoopedInWidget } from "./LoopedInWidget";
import NotificationList from "./NotificationList";

const MainLogo = styled.img`
  height: 45px;
  margin-right: 16px;
  display: block;
`;

MainLogo.defaultProps = {
  src: logoSrc,
};

const Container = styled.div<{ impersonating: boolean }>`
  background: ${(props) =>
    props.impersonating ? props.theme.colors.warning : "white"};
  box-shadow: ${(props) => props.theme.shadows[1]};
  justify-content: space-between;
  flex-direction: row;
  display: flex;
  position: relative;
  z-index: 750;
`;

const CircleButtonElem = styled.button<CircleButtonProps>`
  border: 0;
  background: ${(props) => props.theme.colors.grey3};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 6px;
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  ${space};

  &:hover {
    background-color: ${(props) => props.theme.colors.body};
  }

  background-color: ${(props) => (props.active ? props.theme.colors.body : "")};
`;

const CountBadge = styled.span`
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  box-sizing: border-box;
  border-radius: 999px;
  border: 2px solid white;
  background-color: ${(props) => props.theme.colors.danger};
  color: white;
  font-size: 10px; /* count badge, design-lint-ignore */
  font-weight: 700;
  line-height: 14px;
  text-align: center;
`;

/** Above this the badge shows "9+". */
const MAX_BADGE_COUNT = 9;

type CircleButtonProps = {
  active?: boolean;
} & SpaceProps;

const CircleButton = ({ icon: Icon, count, active, ...rest }: any) => (
  <CircleButtonElem
    aria-label={
      count > 0
        ? `Notifications, ${count} unread`
        : "Notifications, none unread"
    }
    aria-haspopup="dialog"
    {...rest}
    active={active}
  >
    <Icon size="0.8em" aria-hidden="true" />
    {count > 0 && (
      <CountBadge aria-hidden="true">
        {count > MAX_BADGE_COUNT ? `${MAX_BADGE_COUNT}+` : count}
      </CountBadge>
    )}
  </CircleButtonElem>
);

const Separator = styled.span`
  width: 1px;
  height: 30px;
  background-color: ${(props) => props.theme.colors.border};
`;

const TopBar = () => {
  const [user] = useUser();
  const [org] = useOrg();
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  useEffect(() => {
    axios.get("/api/notifications").then(({ data }) => {
      setNotifications(data);
    });
  }, []);

  if (user == null) {
    return null;
  }

  const impersonating = user.authMethod === "impersonated";

  const logoUrl =
    org.theme.logo == null
      ? logoSrc
      : `${process.env.REACT_APP_STATIC_ROOT}/${org.theme.logo}`;

  return (
    <>
      {showNotifications && (
        <NotificationList
          notifications={notifications}
          onClose={() => setShowNotifications(false)}
          onRemove={(id) =>
            setNotifications((old) => old.filter((x) => x.id !== id))
          }
        />
      )}
      <Container impersonating={impersonating}>
        <Flex>
          <Flex alignItems="center" p={[2, 3]}>
            {org.theme.logo != null && org.theme.showCCLogo && (
              <MainLogo alt="Coaching Culture" src={logoSrc} />
            )}
            <Link to="/">
              <MainLogo alt="Company Logo - Dashboard" src={logoUrl} />
            </Link>
            {/* The admin area uses the same accent as the rest of the app,
                so this marker is what shows you're in admin. */}
            <Route path="/success">
              <Label color="black" variant="solid" size="small" ml={3}>
                Admin
              </Label>
            </Route>
          </Flex>
        </Flex>
        {impersonating && (
          <Flex p={[2, 3]}>
            <Text>{`You are currently impersonating ${user.name}`}</Text>
          </Flex>
        )}

        <Flex alignItems="center" p={[2, 3]}>
          <Separator />
          <Route path="/success">
            <LoopedInWidget />
          </Route>
          <CircleButton
            icon={FaBell}
            ml={[2, 3]}
            mr={[2, 3]}
            count={notifications.length}
            active={showNotifications}
            onClick={() => setShowNotifications(true)}
          />
          <Separator />
          <AccountMenu />
        </Flex>
      </Container>
    </>
  );
};

export default TopBar;
